/*
 * School-User One-Off Onboarding Wizard (Phase C).
 * 5 steps: State → School pick → Details → Review & Quote → Checkout.
 * Replaces the old single-school signup form. Creates a quote then routes
 * to the checkout page (Stripe card OR Purchase Order).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PhoneField } from "@/components/PhoneField";
import { toast } from "@/components/ui/sonner";
import { Search, Check, ArrowRight, ArrowLeft, Loader2, Building2 } from "lucide-react";
import { TermsCheckbox } from "../diagnostic/TermsComponents";

const API = process.env.REACT_APP_BACKEND_URL;
const fieldClass = "h-11 bg-white border border-[#e5e7eb] text-[#111827] placeholder:text-[#9ca3af]";
const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];
const STEPS = ["State", "Your School", "Details", "Review & Quote"];

const Stepper = ({ step }) => (
  <div className="flex items-center gap-2 mb-8" data-testid="school-wizard-stepper">
    {STEPS.map((s, i) => (
      <div key={s} className="flex items-center gap-2">
        <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[12px] font-bold ${i <= step ? "bg-[#007a9e] text-white" : "bg-[#e5e7eb] text-[#6b7280]"}`}>
          {i < step ? <Check className="h-4 w-4" /> : i + 1}
        </div>
        <span className={`text-[12px] font-semibold ${i <= step ? "text-[#111827]" : "text-[#9ca3af]"}`}>{s}</span>
        {i < STEPS.length - 1 && <div className="w-6 h-px bg-[#e5e7eb]" />}
      </div>
    ))}
  </div>
);

const Field = ({ label, value, onChange, placeholder, type = "text", required, testid, phone }) => (
  <div>
    <label className="block text-[12px] font-semibold text-[#374151] mb-1.5">{label}{required && " *"}</label>
    {phone ? (
      <PhoneField value={value} onChange={(v) => onChange(v)} placeholder={placeholder} testid={testid} />
    ) : (
      <Input type={type} className={fieldClass} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} />
    )}
  </div>
);

export default function SchoolSignupWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [state, setState] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [manual, setManual] = useState(false);
  const [salesContacts, setSalesContacts] = useState([]);
  const [terms, setTerms] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [singlePrice, setSinglePrice] = useState(4995);
  const [form, setForm] = useState({
    school_name: "", city: "", first_name: "", last_name: "", email: "",
    role_title: "", phone: "", school_website: "",
    ap_contact_name: "", ap_contact_email: "", ap_contact_phone: "",
    sales_contact_id: "", ai_back_office_readiness_response: "",
  });
  const up = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/api/employees/sales-contacts`).then((r) => setSalesContacts(r.data || [])).catch(() => {});
    axios.get(`${API}/api/school-onboarding/pricing`).then((r) => {
      const t1 = (r.data?.tiers || []).find((t) => t.tier_start === 1);
      if (t1?.rate) setSinglePrice(t1.rate);
    }).catch(() => {});
    // Prefill from a tracked lead link (attribution-preserving).
    const lt = new URLSearchParams(window.location.search).get("lt") || sessionStorage.getItem("lead_token");
    if (lt) {
      axios.get(`${API}/api/lead-link/${lt}`).then((r) => {
        const p = r.data?.prefill; if (!p) return;
        setForm((f) => ({ ...f,
          school_name: p.organization_name || f.school_name, first_name: p.first_name || f.first_name,
          last_name: p.last_name || f.last_name, email: p.email || f.email,
          phone: p.phone || f.phone, role_title: p.title || f.role_title }));
        if (p.state) setState(p.state);
      }).catch(() => {});
    }
  }, []);

  const runSearch = async () => {
    if (!state) return;
    setSearching(true);
    try {
      const r = await axios.get(`${API}/api/school-onboarding/schools`, { params: { state, q, page_size: 20 } });
      setResults(r.data.schools || []);
    } catch { toast.error("Search failed"); } finally { setSearching(false); }
  };

  const choose = (s) => {
    setPicked(s);
    setForm((f) => ({ ...f, school_name: s.school_name, city: s.city || "" }));
  };

  const submitQuote = async () => {
    for (const k of ["first_name", "last_name", "email", "school_name"]) {
      if (!form[k]) { toast.error("Please complete all required fields"); return; }
    }
    if (!form.ai_back_office_readiness_response) { toast.error("Please answer the AI back-office question"); return; }
    if (!terms) { toast.error("Please accept the Terms & Conditions"); return; }
    setSubmitting(true);
    try {
      const contact = salesContacts.find((c) => c.id === form.sales_contact_id);
      const r = await axios.post(`${API}/api/school-onboarding/quote`, {
        ...form, state, school_directory_id: picked?.id || null,
        sales_contact_name: contact?.name || "",
        accepted_terms_id: terms.terms_document_id, accepted_terms_version: terms.terms_version,
      });
      const lt = new URLSearchParams(window.location.search).get("lt") || sessionStorage.getItem("lead_token");
      if (lt) { axios.post(`${API}/api/lead-link/${lt}/converted`, { email: form.email }).catch(() => {}); sessionStorage.removeItem("lead_token"); }
      navigate(`/myschoolrx/checkout/${r.data.quote.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create your quote. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#f9fafb]" data-testid="school-signup-wizard">
      <header className="sticky top-0 z-30 bg-white border-b border-[#e5e7eb]">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 flex items-center justify-between h-20">
          <button onClick={() => navigate("/myschoolrx")} className="flex items-center" data-testid="wizard-logo">
            <img src="/assets/myschoolrx-wordmark-600.png" alt="MySchoolRx" className="h-10 w-auto" />
          </button>
          <a href="/diagnostic/login" className="text-[13px] font-semibold text-[#374151] hover:text-[#111827]">Sign in</a>
        </div>
      </header>

      <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
        <Stepper step={step} />
        <div className="bg-white rounded-2xl border border-[#e5e7eb] p-6 sm:p-8">
          {step === 0 && (
            <div data-testid="wizard-step-state">
              <h1 className="text-2xl font-bold text-[#111827]">What state is your school in?</h1>
              <p className="mt-2 text-[14px] text-[#6b7280]">We'll help you find your school in our directory.</p>
              <div className="mt-6">
                <label htmlFor="wizard-state-select" className="block text-[12px] font-semibold text-[#374151] mb-1.5">State *</label>
                <select id="wizard-state-select" value={state} onChange={(e) => setState(e.target.value)} className="h-11 w-full bg-white border border-[#e5e7eb] rounded-md text-[14px] px-3" data-testid="wizard-state-select">
                  <option value="">Select your state…</option>
                  {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="mt-8 flex justify-end">
                <Button disabled={!state} onClick={() => { setStep(1); runSearch(); }} className="bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-50" data-testid="wizard-state-next">
                  Continue <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 1 && (
            <div data-testid="wizard-step-school">
              <h1 className="text-2xl font-bold text-[#111827]">Find your school</h1>
              <p className="mt-2 text-[14px] text-[#6b7280]">Search {state} schools, or add yours manually.</p>
              {!manual ? (
                <>
                  <div className="mt-5 flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#9ca3af]" />
                      <Input className={`${fieldClass} pl-9`} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="School name or city" data-testid="wizard-school-search" />
                    </div>
                    <Button onClick={runSearch} variant="outline" data-testid="wizard-school-search-btn">{searching ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}</Button>
                  </div>
                  <div className="mt-4 max-h-72 overflow-y-auto space-y-2" data-testid="wizard-school-results">
                    {results.map((s) => (
                      <button key={s.id} onClick={() => choose(s)} className={`w-full text-left rounded-lg border p-3 flex items-center gap-3 ${picked?.id === s.id ? "border-[#007a9e] bg-[#f0f9fc]" : "border-[#e5e7eb] hover:border-[#007a9e]/40"}`} data-testid={`wizard-school-option-${s.id}`}>
                        <Building2 className="h-4 w-4 text-[#007a9e] shrink-0" />
                        <div className="min-w-0">
                          <div className="text-[14px] font-semibold text-[#111827] truncate">{s.school_name}</div>
                          <div className="text-[12px] text-[#6b7280]">{s.city}{s.city && ", "}{s.state}{s.school_type ? ` · ${s.school_type}` : ""}</div>
                        </div>
                        {picked?.id === s.id && <Check className="h-4 w-4 text-[#007a9e] ml-auto" />}
                      </button>
                    ))}
                    {!searching && results.length === 0 && <div className="text-[13px] text-[#6b7280] py-4 text-center">No results yet — search above.</div>}
                  </div>
                  <button onClick={() => { setManual(true); setPicked(null); }} className="mt-3 text-[13px] font-semibold text-[#007a9e] hover:underline" data-testid="wizard-manual-toggle">My school isn't listed →</button>
                </>
              ) : (
                <div className="mt-5 space-y-4" data-testid="wizard-manual-entry">
                  <Field label="School name" value={form.school_name} onChange={up("school_name")} required testid="wizard-manual-school-name" />
                  <Field label="City" value={form.city} onChange={up("city")} testid="wizard-manual-city" />
                  <button onClick={() => setManual(false)} className="text-[13px] font-semibold text-[#374151] hover:underline">← Back to search</button>
                </div>
              )}
              <div className="mt-8 flex justify-between">
                <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
                <Button disabled={!form.school_name} onClick={() => setStep(2)} className="bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-50" data-testid="wizard-school-next">
                  Continue <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="wizard-step-details">
              <h1 className="text-2xl font-bold text-[#111827]">Your details</h1>
              <div className="rounded-lg bg-[#f9fafb] border border-[#e5e7eb] px-3 py-2 text-[13px] text-[#374151]">
                <span className="font-semibold">{form.school_name}</span>{form.city ? ` · ${form.city}` : ""} · {state}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="First name" value={form.first_name} onChange={up("first_name")} required testid="wizard-first-name" />
                <Field label="Last name" value={form.last_name} onChange={up("last_name")} required testid="wizard-last-name" />
              </div>
              <Field label="Work email" value={form.email} onChange={up("email")} type="email" required testid="wizard-email" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Role / Title" value={form.role_title} onChange={up("role_title")} placeholder="e.g., CEO" testid="wizard-role" />
                <Field label="Phone" value={form.phone} onChange={up("phone")} testid="wizard-phone" phone />
              </div>
              <Field label="School website" value={form.school_website} onChange={up("school_website")} placeholder="schoolname.org" testid="wizard-website" />

              <div className="pt-2 border-t border-[#e5e7eb]">
                <div className="text-[12px] font-semibold uppercase tracking-wide text-[#6b7280] mt-3 mb-2">AP / Business Office Contact (for Purchase Order)</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Field label="AP contact name" value={form.ap_contact_name} onChange={up("ap_contact_name")} testid="wizard-ap-name" />
                  <Field label="AP contact email" value={form.ap_contact_email} onChange={up("ap_contact_email")} testid="wizard-ap-email" />
                </div>
              </div>

              <div className="pt-2">
                <label htmlFor="wizard-ai-question" className="block text-[12px] font-semibold text-[#374151] mb-1.5">Could your school benefit from using AI to streamline back office operations? *</label>
                <select id="wizard-ai-question" value={form.ai_back_office_readiness_response} onChange={(e) => up("ai_back_office_readiness_response")(e.target.value)} className="h-11 w-full bg-white border border-[#e5e7eb] rounded-md text-[14px] px-3" data-testid="wizard-ai-question">
                  <option value="">Select one…</option>
                  <option value="yes_not_optimized">Yes - we are not using AI in the best way</option>
                  <option value="no_already_optimized">No - we already make good use of AI</option>
                  <option value="unsure_where_to_start">We're not sure where to start with AI</option>
                </select>
              </div>

              {salesContacts.length > 0 && (
                <div className="pt-1">
                  <label htmlFor="wizard-sales-contact" className="block text-[12px] font-semibold text-[#374151] mb-1.5">Sales Contact (optional)</label>
                  <select id="wizard-sales-contact" value={form.sales_contact_id} onChange={(e) => up("sales_contact_id")(e.target.value)} className="h-11 w-full bg-white border border-[#e5e7eb] rounded-md text-[14px] px-3" data-testid="wizard-sales-contact">
                    <option value="">Direct Sale / No Sales Contact</option>
                    {salesContacts.map((c) => <option key={c.id} value={c.id}>{c.name}{c.title ? ` — ${c.title}` : ""}</option>)}
                  </select>
                </div>
              )}

              <div className="pt-2"><TermsCheckbox productType="caporilli_diagnostic" value={terms} onChange={setTerms} /></div>

              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
                <Button onClick={() => setStep(3)} className="bg-[#007a9e] hover:bg-[#00607d] text-white" data-testid="wizard-details-next">
                  Review Quote <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div data-testid="wizard-step-quote">
              <h1 className="text-2xl font-bold text-[#111827]">Your quote</h1>
              <div className="mt-5 rounded-xl border border-[#e5e7eb] p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[15px] font-bold text-[#111827]">{form.school_name}</div>
                    <div className="text-[13px] text-[#6b7280]">{form.city ? `${form.city}, ` : ""}{state}</div>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between border-t border-[#e5e7eb] pt-4">
                  <span className="text-[14px] text-[#374151]">MySchoolRx — Annual License (1 school)</span>
                  <span className="text-[18px] font-bold text-[#111827]" data-testid="wizard-quote-total">{"$" + Number(singlePrice).toLocaleString()}</span>
                </div>
                <p className="mt-2 text-[11px] text-[#9ca3af]">Includes up to 5 users. Taxes calculated at invoice time. AI token & storage billed by usage.</p>
              </div>
              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
                <Button disabled={submitting} onClick={submitQuote} className="bg-[#007a9e] hover:bg-[#00607d] text-white disabled:opacity-50" data-testid="wizard-quote-continue">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating…</> : <>Continue to Checkout <ArrowRight className="h-4 w-4 ml-2" /></>}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
